import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewContainerRef,
    ViewChild,
    OnChanges,
    SimpleChanges,
    ComponentRef,
    Type,
    OnDestroy,
    ElementRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-card-outlet',
    standalone: true,
    imports: [CommonModule],
    template: `<ng-container #container></ng-container>`,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
            }
            :host.highlighted {
                border-radius: 32px;
                outline: 2px solid var(--p3-40);
            }
        `,
    ],
})
export class CardOutletComponent implements OnChanges, OnDestroy {
    @Input() component!: Type<any>;
    @Input() inputs: Record<string, any> = {};
    @Input() outputs: Record<string, string> = {};
    @Input() highlighted = false;
    @Output() outputEvent = new EventEmitter<string>();

    @ViewChild('container', { read: ViewContainerRef, static: true })
    container!: ViewContainerRef;

    private componentRef: ComponentRef<any> | null = null;
    private hostEl = inject(ElementRef);

    ngOnChanges(changes: SimpleChanges) {
        if (changes['component']) {
            this.createComponent();
        }
        if (changes['inputs'] && this.componentRef) {
            this.updateInputs();
        }
        if (changes['highlighted'] && this.componentRef) {
            this.updateHighlight();
        }
    }

    private createComponent() {
        this.container.clear();
        if (!this.component) return;

        this.componentRef = this.container.createComponent(this.component);
        this.updateInputs();
        this.bindOutputs();
        this.updateHighlight();
    }

    private updateInputs() {
        if (!this.componentRef) return;
        for (const [key, value] of Object.entries(this.inputs)) {
            this.componentRef.setInput(key, value);
        }
        this.componentRef.changeDetectorRef.markForCheck();
    }

    private updateHighlight() {
        const host = this.hostEl.nativeElement as HTMLElement;
        if (this.highlighted) {
            host.classList.add('highlighted');
        } else {
            host.classList.remove('highlighted');
        }
    }

    private bindOutputs() {
        if (!this.componentRef || !this.outputs) return;
        for (const [outputName, methodName] of Object.entries(this.outputs)) {
            const output = this.componentRef.instance[outputName];
            if (output?.subscribe) {
                output.subscribe(() => this.outputEvent.emit(methodName));
            }
        }
    }

    ngOnDestroy() {
        this.container.clear();
        this.componentRef = null;
    }
}
